import { TabItem } from '@/types/common';
import { defineAsyncComponent } from 'vue';
import { PermissionsMBLFunctions } from '@/constants/permissions';
import {
  getDefaultImportWeightUnit,
  getDefaultImportVolumeUnit,
} from '@/helpers/settings/measurementUnits';

export const MBL_VIEW_TABS = {
  mblDetails: 'MBL_DETAILS',
  hblList: 'HBL_LIST',
  milestones: 'MILESTONES',
  charges: 'CHARGES',
  attachments: 'ATTACHMENTS',
  photos: 'PHOTOS',
  pttApplication: 'PTT_APPLICATION',
  print: 'PRINT',
};

export const MBL_VIEW_TABS_OPTIONS: TabItem[] = [
  { text: 'MblDetails', value: MBL_VIEW_TABS.mblDetails },
  {
    text: 'HblList',
    value: MBL_VIEW_TABS.hblList,
    permissionKey: PermissionsMBLFunctions.MBL_HBL_LIST,
  },
  {
    text: 'Milestones',
    value: MBL_VIEW_TABS.milestones,
    permissionKey: PermissionsMBLFunctions.MBL_MILESTONES,
  },
  {
    text: 'Charges',
    value: MBL_VIEW_TABS.charges,
    permissionKey: [
      PermissionsMBLFunctions.MBL_AUTO_CHARGES,
      PermissionsMBLFunctions.MBL_MANUAL_CHARGES,
    ],
  },
  {
    text: 'Attachments',
    value: MBL_VIEW_TABS.attachments,
    permissionKey: PermissionsMBLFunctions.MBL_ATTACHMENTS,
  },
  {
    text: 'PTT Application',
    value: MBL_VIEW_TABS.pttApplication,
    permissionKey: PermissionsMBLFunctions.MBL_PTT,
  },
  { text: 'Print', value: MBL_VIEW_TABS.print, permissionKey: PermissionsMBLFunctions.MBL_PRINT },
];

export const MBL_VIEW_TABS_TABLET_OPTIONS: TabItem[] = [
  {
    text: 'HblList',
    value: MBL_VIEW_TABS.hblList,
    permissionKey: PermissionsMBLFunctions.MBL_HBL_LIST,
  },
  {
    text: 'Photos',
    value: MBL_VIEW_TABS.photos,
  },
  {
    text: 'Notes',
    value: MBL_VIEW_TABS.mblDetails,
    permissionKey: PermissionsMBLFunctions.MBL_NOTES,
  },
];

export const MBL_FORM_STEPS = [
  {
    label: 'PolPodDetails',
    isValid: true,
    permissionKey: PermissionsMBLFunctions.MBL_POL_POD_DETAILS,
    component: defineAsyncComponent(
      () => import('@/components/master-bl/forms/components/PolPodDetailsFormComponent.vue')
    ),
  },
  {
    label: 'ContainerDetails',
    isValid: true,
    permissionKey: PermissionsMBLFunctions.MBL_CONTAINER_DETAILS,
    component: defineAsyncComponent(
      () => import('@/components/master-bl/forms/components/ContainerDetailsFormComponent.vue')
    ),
  },
  {
    label: 'DischargeDetails',
    isValid: true,
    permissionKey: PermissionsMBLFunctions.MBL_DISCHARGE_DETAILS,
    component: defineAsyncComponent(
      () => import('@/components/master-bl/forms/components/DischargeDetailsFormComponent.vue')
    ),
  },
  {
    label: 'CFSReceipt',
    isValid: true,
    permissionKey: PermissionsMBLFunctions.MBL_CFS_RECEIPT,
    component: defineAsyncComponent(
      () => import('@/components/master-bl/forms/components/CFSReceiptFormComponent.vue')
    ),
  },
  {
    label: 'Notes',
    isValid: true,
    permissionKey: PermissionsMBLFunctions.MBL_NOTES,
    component: defineAsyncComponent(
      () => import('@/components/master-bl/forms/components/AdditionalNotesFormComponent.vue')
    ),
  },
];

export const MBL_FORM_DEFAULT_DATA = () => {
  return {
    pod: null,
    warehouse: null,
    service: null,
    custRef: '',
    customerContactName: '',
    paymentLookup: null,
    division: null,
    customer: null,
    mblNumber: '',
    carrier: null,
    mib: '',
    vessel: {
      vessel_name: '',
      imo: '',
      mmsi: '',
    },
    voyage: '',
    pol: null,
    sailDate: null,
    eta: '',
    containerType: null,
    containerNumber: '',
    sealNumber: '',
    noOfPcs: 0,
    packageType: null,
    measure: 0,
    weight: 0,
    weightUnit: getDefaultImportWeightUnit(),
    measureUnit: getDefaultImportVolumeUnit(),
    pttRequiredDate: null,
    pttApprovalDate: null,
    hazmat: false,
    headLoad: false,
    dischargeDate: null,
    clearedCbpExamDate: null,
    devanStartTime: null,
    devanEndTime: null,
    devanHours: 0,
    terminal: null,
    appointmentDate: null,
    lastFreeDate: null,
    pickupCarrier: null,
    terminalDispatchDate: null,
    destinationWarehouse: null,
    deliveryNotes: '',
    cfsReceivedDate: null,
    cfsLocation: '',
    moveToDoorDate: null,
    door: '',
    tir: '',
    tirDate: null,
    chassis: '',
    stripDate: null,
    strippedBy: '',
    devanLabourCost: '',
    labour: [],
    headLoadDate: null,
    headLoadFreeDate: null,
    emptyReadyForPickupDate: null,
    emptyReturnDate: null,
    emptyReturnLocation: null,
    documentReceivedDate: new Date(),
    terminalAppointmentDate: null,
    otherEmptyReturnLocation: '',
    goDate: null,
    internalNotes: '',
    externalNotes: '',
    dispatchNotes: '',
    containerPics: [],
    containerNumberCheck: false,
    overridePttDates: false,
    cargoDescription: 'FAK',
    loadType: 'Mixed',
    chassisRentalDays: '',
    warehouseLoc: null,
    warehouseLfd: null,
    overrideLfd: false,
    availableDate: null,
    isPttRequired: true,
    tariffEffectiveDate: new Date(),
    etaTimeZone: '',
    goDateTimeZone: '',
    tirDateTimeZone: '',
    sailDateTimeZone: '',
    closeDateTimeZone: '',
    stripDateTimeZone: '',
    devanEndTimeTimeZone: '',
    warehouseLfdTimeZone: '',
    lastFreeDateTimeZone: '',
    headLoadDateTimeZone: '',
    dischargeDateTimeZone: '',
    availableDateTimeZone: '',
    devanStartTimeTimeZone: '',
    moveToDoorDateTimeZone: '',
    appointmentDateTimeZone: '',
    emptyReturnDateTimeZone: '',
    pttRequiredDateTimeZone: '',
    cfsReceivedDateTimeZone: '',
    pttApprovalDateTimeZone: '',
    headLoadFreeDateTimeZone: '',
    emptyReadyForPickupDateTimeZone: '',
    clearedCbpExamDateTimeZone: '',
    documentReceivedDateTimeZone: '',
    terminalDispatchDateTimeZone: '',
    terminalAppointmentDateTimeZone: '',
    tariffEffectiveDateTimeZone: '',
    isContainerNotReady: false,
    containerReadyDate: '',
    containerReadyDateTimeZone: '',
  };
};

export const MBL_DEFAULT_FILTERS = () => {
  return {
    pageNumber: 1,
    itemsPerPage: 10,
    sortBy: {
      name: 'createdAt',
      order: -1,
    },
  };
};

export const MBL_FORM_DEFAULT_CARDS = () => {
  return [
    {
      text: 'Internal Notes',
      value: 'internalNotes',
      permissionKey: PermissionsMBLFunctions.MBL_INTERNAL_NOTES,
    },
    {
      text: 'External Notes',
      value: 'externalNotes',
      permissionKey: PermissionsMBLFunctions.MBL_EXTERNAL_NOTES,
    },
    {
      text: 'Dispatch Notes',
      value: 'dispatchNotes',
      permissionKey: PermissionsMBLFunctions.MBL_DISPATCH_NOTES,
    },
    {
      text: 'Delivery Notes',
      value: 'deliveryNotes',
      permissionKey: PermissionsMBLFunctions.MBL_DELIVERY_NOTES,
    },
  ];
};
